import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Mail, Phone, MapPin, FileText, Briefcase, Sparkles, Plus, Archive, ArchiveRestore, Receipt, ArrowRight, Pencil, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import FileUploader from "@/components/FileUploader";
import SmsThread from "@/components/SmsThread";
import NewInvoiceDialog from "@/components/NewInvoiceDialog";
import StreetViewPanel from "@/components/StreetViewPanel";
import CustomerReviewsSection from "@/components/CustomerReviewsSection";
import CustomerPropertiesSection from "@/components/CustomerPropertiesSection";
import PropertyMeasurementsSection from "@/components/PropertyMeasurementsSection";
import PropertySolarCard from "@/components/PropertySolarCard";
import PropertyValueSummary from "@/components/PropertyValueSummary";
import EmailComposeDialog from "@/components/EmailComposeDialog";
import EmailThread from "@/components/EmailThread";
import CustomerEmailTab from "@/components/CustomerEmailTab";
import CustomerCommsPanel from "@/components/CustomerCommsPanel";
import { CustomerPaymentsSection, useCustomerPayments } from "@/components/CustomerPaymentsSection";
import { fmtPaidDate } from "@/components/PaymentDateEditor";

const EST_STATUS = {
  draft: "bg-zinc-100 text-zinc-800 border-zinc-200",
  sent: "bg-blue-100 text-blue-800 border-blue-200",
  viewed: "bg-amber-100 text-amber-800 border-amber-200",
  accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  declined: "bg-rose-100 text-rose-800 border-rose-200",
  expired: "bg-zinc-200 text-zinc-700 border-zinc-300",
};

const JOB_STATUS = {
  scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  canceled: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function CustomerProfile() {
  const { id } = useParams();
  const [payData, reloadPayments] = useCustomerPayments(id);
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  // Only sales+ (sales/admin/owner) can edit or archive; hide these buttons
  // for lower roles so the UI does not expose actions they can't perform.
  const canEdit = user && ["sales", "admin", "owner"].includes(user.role);
  const canArchive = canEdit;

  const load = async () => {
    try {
      const { data } = await api.get(`/customers/${id}/profile`);
      setData(data);
    } catch { toast.error("Customer not found"); nav("/app/customers"); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const { data } = await api.get(`/estimator/properties?customer_id=${id}`);
        setProperties(Array.isArray(data) ? data : (data?.properties || []));
      } catch { setProperties([]); }
    })();
  }, [id]);

  if (!data) return <div className="p-8 text-zinc-500">Loading…</div>;
  const { customer, lead, activities, estimates, jobs, stats } = data;
  const invoices = data.invoices || [];

  const INV_STATUS = {
    draft: "bg-zinc-100 text-zinc-800 border-zinc-200",
    sent: "bg-blue-100 text-blue-800 border-blue-200",
    viewed: "bg-amber-100 text-amber-800 border-amber-200",
    paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
    partial: "bg-amber-100 text-amber-800 border-amber-200",
    overdue: "bg-rose-100 text-rose-800 border-rose-200",
    void: "bg-zinc-200 text-zinc-700 border-zinc-300",
  };

  const convertEstimateToInvoice = async (estimate) => {
    // Prevent accidental duplicates — the button flips to "View invoice"
    // when a linked invoice already exists, but double-catch here too in
    // case optimistic navigation happens.
    if (estimate.converted_to_invoice_id) {
      nav(`/app/invoices/${estimate.converted_to_invoice_id}`);
      return;
    }
    try {
      const { data: inv } = await api.post(`/estimates/${estimate.id}/create-invoice`);
      toast.success(`Invoice ${inv.invoice_number} created from ${estimate.estimate_number}`);
      nav(`/app/invoices/${inv.id}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "";
      toast.error(`Convert to invoice failed${status ? ` (${status})` : ""}: ${detail}`);
    }
  };

  const createInvoiceForCustomer = () => {
    // If the customer has estimates that haven't been converted yet, prompt
    // the user to convert an existing estimate — that's the "as few clicks
    // as possible" path. If they'd rather start blank, they can dismiss.
    const unconverted = (estimates || []).filter(e => !e.converted_to_invoice_id);
    if (unconverted.length > 0) {
      const one = unconverted[0];
      const useEstimate = window.confirm(
        `You have ${unconverted.length} estimate${unconverted.length === 1 ? "" : "s"} for this customer that hasn't been invoiced.\n\n` +
        `Click OK to convert "${one.title || one.estimate_number}" (${one.estimate_number}) to an invoice.\n` +
        `Click Cancel to start a blank invoice with the customer's info pre-filled.`
      );
      if (useEstimate) {
        convertEstimateToInvoice(one);
        return;
      }
    }
    // Blank invoice path — open dialog to enter line items directly.
    setInvoiceDialogOpen(true);
  };

  const openEdit = () => {
    setEditForm({
      name: customer.name || "",
      company: customer.company || "",
      email: customer.email || "",
      phone: customer.phone || "",
      address: customer.address || "",
      lead_source: customer.lead_source || "",
      notes: customer.notes || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) { toast.error("Name is required"); return; }
    setEditSaving(true);
    try {
      await api.put(`/customers/${customer.id}`, {
        name: editForm.name.trim(),
        company: (editForm.company || "").trim(),
        email: (editForm.email || "").trim(),
        phone: (editForm.phone || "").trim(),
        address: (editForm.address || "").trim(),
        lead_source: (editForm.lead_source || "").trim(),
        notes: editForm.notes || "",
      });
      toast.success("Customer updated");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update customer");
    } finally { setEditSaving(false); }
  };

  return (
    <div className="p-8 max-w-7xl">
      <button onClick={()=>nav("/app/customers")} className="text-sm text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4" data-testid="customer-back-btn">
        <ArrowLeft className="w-4 h-4"/> All customers
      </button>

      {/* Header row: customer info on the left, Street View on the right.
          Stacks below on mobile via `lg:grid-cols-[minmax(0,1fr)_420px]`. */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-6 mb-6 items-start">
        <div>
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-zinc-900 flex items-center justify-center text-white text-2xl font-bold font-heading">
                {(customer.name||"?").slice(0,1).toUpperCase()}
              </div>
              <div>
                <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Customer 360</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-heading text-4xl font-bold" data-testid="customer-header-name">{customer.name}</h1>
                  {canEdit && (
                    <button
                      onClick={openEdit}
                      className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-[#FF5A00] border border-zinc-200 hover:border-[#FF5A00] rounded-md px-2 py-1 transition-colors"
                      data-testid="customer-edit-btn"
                      aria-label="Edit customer details"
                    >
                      <Pencil className="w-3 h-3"/> Edit
                    </button>
                  )}
                </div>
                {customer.company && (
                  <div className="text-sm text-zinc-700 font-medium mt-1" data-testid="customer-header-company">
                    {customer.company}
                  </div>
                )}
                <div className="flex items-center gap-4 mt-2 text-sm text-zinc-600 flex-wrap">
                  {customer.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5"/>{customer.email}</span>}
                  {customer.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5"/>{customer.phone}</span>}
                  {customer.address && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/>{customer.address}</span>}
                  {!customer.email && !customer.phone && !customer.address && canEdit && (
                    <button onClick={openEdit} className="text-[#FF5A00] hover:underline text-xs" data-testid="customer-add-details-cta">
                      + Add contact details
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {canArchive && (customer.archived ? (
                <Button
                  onClick={async () => {
                    try { await api.post(`/customers/${customer.id}/restore`); toast.success("Customer restored"); load(); }
                    catch { toast.error("Failed to restore"); }
                  }}
                  variant="outline"
                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                  data-testid="customer-restore-btn"
                >
                  <ArchiveRestore className="w-4 h-4 mr-1"/> Restore customer
                </Button>
              ) : (
                <Button
                  onClick={async () => {
                    if (!window.confirm(`Archive ${customer.name}? They will be hidden from active lists but all history is preserved.`)) return;
                    try { await api.post(`/customers/${customer.id}/archive`); toast.success("Customer archived"); load(); }
                    catch { toast.error("Failed to archive"); }
                  }}
                  variant="outline"
                  className="text-zinc-500"
                  data-testid="customer-archive-btn"
                >
                  <Archive className="w-4 h-4 mr-1"/> Archive
                </Button>
              ))}
              <Button onClick={()=>nav(`/app/jobs?customer_id=${customer.id}`)} variant="outline" data-testid="customer-new-job-btn">
                <Briefcase className="w-4 h-4 mr-1"/> New job
              </Button>
              <Button onClick={()=>nav(`/app/customers/${customer.id}/estimator`)} variant="outline" className="border-[#FF5A00] text-[#FF5A00] hover:bg-orange-50" data-testid="customer-smart-estimator-btn">
                <Sparkles className="w-4 h-4 mr-1"/> Smart Estimator
              </Button>
              <Button onClick={()=>nav(`/app/estimates/new?customer_id=${customer.id}`)} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="customer-new-estimate-btn">
                <Plus className="w-4 h-4 mr-1"/> New estimate
              </Button>
              <Button onClick={createInvoiceForCustomer} className="bg-emerald-600 hover:bg-emerald-700 text-white btn-lift" data-testid="customer-create-invoice-btn">
                <Receipt className="w-4 h-4 mr-1"/> Create invoice
              </Button>
              <CustomerCommsPanel
                customerId={customer.id}
                customer={customer}
                triggerLabel="Open SMS thread"
                testId="customer-open-sms-btn"
              />
              {customer?.email && (
                <Button
                  variant="outline"
                  onClick={() => setEmailOpen(true)}
                  className="border-sky-200 text-sky-700 hover:bg-sky-50"
                  data-testid="customer-email-btn"
                >
                  <Mail className="w-4 h-4 mr-1"/> Email
                </Button>
              )}
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const { data: p } = await api.get(`/customers/${customer.id}/thank-you-review/preview`);
                    if (!p.review_url_configured) { toast.error("Add your Google Review URL in Settings → Payments"); return; }
                    const warn = p.last_sent_at
                      ? `Already sent on ${new Date(p.last_sent_at).toLocaleDateString()} — send again?`
                      : `Send this Thank-You + Review text?\n\n${p.body}`;
                    if (!window.confirm(warn)) return;
                    await api.post(`/customers/${customer.id}/thank-you-review/send`, { force: !!p.last_sent_at });
                    toast.success("Thank-You text sent");
                  } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                }}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="customer-send-thank-you-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1"/> Send Thank You text
              </Button>
            </div>
          </div>
        </div>
        {/* Street View — mounted here so it appears next to the customer
            information on desktop, stacks below on mobile. Reuses the
            saved customer.address (and, when present on the record, the
            already-known coordinates so no re-geocode is needed). */}
        <StreetViewPanel
          address={customer.address || ""}
          coord={
            (customer.latitude != null && customer.longitude != null)
              ? { lat: customer.latitude, lng: customer.longitude }
              : null
          }
          title="Street View"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
        <MiniStat label="Estimates" value={stats.estimates_count} />
        <MiniStat label="Invoices" value={stats.invoices_count ?? invoices.length} />
        <MiniStat label="Jobs" value={stats.jobs_count} />
        <MiniStat label="Open value" value={`$${(stats.open_value||0).toLocaleString()}`} accent="orange"/>
        <MiniStat label="Lifetime value" value={`$${(stats.lifetime_value||0).toLocaleString()}`} accent="emerald"/>
        <button type="button" className="text-left bg-white border border-zinc-200 rounded-lg p-4 card-crisp hover:border-emerald-300 transition-colors" onClick={() => document.getElementById("customer-payments")?.scrollIntoView({ behavior: "smooth" })} data-testid="customer-payments-stat">
          <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">Paid to date</div>
          <div className="font-heading text-2xl font-bold mt-1 text-emerald-700">{payData ? `$${(payData.total_paid||0).toLocaleString()}` : "…"}</div>
          <div className="text-[10px] font-mono-accent text-zinc-500 mt-0.5" data-testid="customer-last-payment-date">{payData?.last_paid_date ? `Last ${fmtPaidDate(payData.last_paid_date)}` : "No payments"}</div>
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Estimates */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-[#FF5A00]"/>
              <h2 className="font-heading text-xl font-bold">Estimates</h2>
              <span className="ml-auto text-xs text-zinc-500 font-mono-accent">{estimates.length}</span>
            </div>
            {estimates.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4">No estimates yet.</div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {estimates.map((e) => (
                  <div key={e.id} className="flex items-center justify-between py-3 hover:bg-zinc-50 px-2 rounded" data-testid={`profile-estimate-${e.id}`}>
                    <button onClick={()=>nav(`/app/estimates/${e.id}`)} className="flex-1 text-left flex items-center gap-3 min-w-0">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{e.title || e.estimate_number}</div>
                        <div className="text-xs text-zinc-500 font-mono-accent">{e.estimate_number}</div>
                      </div>
                    </button>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="font-mono-accent text-sm">${(e.total||0).toLocaleString()}</span>
                      <span className={`px-2 py-0.5 text-xs border rounded ${EST_STATUS[e.status] || "bg-zinc-100"}`}>{e.status}</span>
                      {e.converted_to_invoice_id ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(ev)=>{ ev.stopPropagation(); nav(`/app/invoices/${e.converted_to_invoice_id}`); }}
                          data-testid={`profile-estimate-view-invoice-${e.id}`}
                          title="This estimate has already been converted to an invoice"
                        >
                          <ArrowRight className="w-3.5 h-3.5 mr-1"/> View invoice
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={(ev)=>{ ev.stopPropagation(); convertEstimateToInvoice(e); }}
                          data-testid={`profile-estimate-convert-${e.id}`}
                        >
                          <Receipt className="w-3.5 h-3.5 mr-1"/> Convert to invoice
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Invoices */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="customer-invoices-section">
            <div className="flex items-center gap-2 mb-4">
              <Receipt className="w-5 h-5 text-emerald-600"/>
              <h2 className="font-heading text-xl font-bold">Invoices</h2>
              <span className="ml-auto text-xs text-zinc-500 font-mono-accent">{invoices.length}</span>
            </div>
            {invoices.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4">No invoices yet. Use <em>Create invoice</em> above to bill this customer.</div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {invoices.map((i) => (
                  <button
                    key={i.id}
                    onClick={()=>nav(`/app/invoices/${i.id}`)}
                    className="w-full flex items-center justify-between py-3 hover:bg-zinc-50 px-2 rounded text-left"
                    data-testid={`profile-invoice-${i.id}`}
                  >
                    <div>
                      <div className="font-medium">{i.title || i.invoice_number}</div>
                      <div className="text-xs text-zinc-500 font-mono-accent">
                        {i.invoice_number}
                        {i.estimate_id && <span className="ml-2 text-emerald-700">↳ from estimate</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono-accent text-sm">${(i.total||0).toLocaleString()}</span>
                      <span className={`px-2 py-0.5 text-xs border rounded ${INV_STATUS[i.status] || "bg-zinc-100"}`}>{i.status}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <CustomerPaymentsSection data={payData} onSaved={reloadPayments} />

          {/* Jobs */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp">
            <div className="flex items-center gap-2 mb-4">
              <Briefcase className="w-5 h-5 text-[#FF5A00]"/>
              <h2 className="font-heading text-xl font-bold">Jobs</h2>
              <span className="ml-auto text-xs text-zinc-500 font-mono-accent">{jobs.length}</span>
            </div>
            {jobs.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4">No jobs yet.</div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {jobs.map((j) => (
                  <button key={j.id} onClick={()=>nav(`/app/jobs/${j.id}`)} className="w-full flex items-center justify-between py-3 hover:bg-zinc-50 px-2 rounded text-left" data-testid={`profile-job-${j.id}`}>
                    <div>
                      <div className="font-medium">{j.title}</div>
                      <div className="text-xs text-zinc-500">{j.scheduled_start ? `Starts ${new Date(j.scheduled_start).toLocaleDateString()}` : "Unscheduled"}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono-accent text-sm">${(j.value||0).toLocaleString()}</span>
                      <span className={`px-2 py-0.5 text-xs border rounded ${JOB_STATUS[j.status] || "bg-zinc-100"}`}>{j.status.replace(/_/g," ")}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {lead && (
            <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp">
              <div className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 mb-2">Linked lead</div>
              <button onClick={()=>nav(`/app/leads/${lead.id}`)} className="text-left w-full hover:bg-zinc-50 rounded p-1" data-testid="profile-lead-link">
                <div className="font-medium">{lead.customer_name}</div>
                <div className="text-xs text-zinc-500 capitalize">{(lead.status||"").replace(/_/g," ")} · {lead.service_requested || "no service"}</div>
              </button>
              {customer.lead_source && <div className="mt-3 text-xs text-zinc-500">Source: <span className="capitalize">{customer.lead_source.replace(/_/g," ")}</span></div>}
            </div>
          )}

          {/* Activity */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-[#FF5A00]"/>
              <h2 className="font-heading text-xl font-bold">Communications</h2>
            </div>
            {activities.length === 0 ? (
              <div className="text-sm text-zinc-500">No activity yet.</div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {activities.map((a) => (
                  <div key={a.id} className="flex gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#FF5A00] mt-2 shrink-0"/>
                    <div className="min-w-0">
                      <div className="text-sm text-zinc-800">{a.content}</div>
                      <div className="text-[10px] text-zinc-500 font-mono-accent uppercase tracking-widest">{a.type} · {new Date(a.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Files & photos */}
          <FileUploader resourceType="customer" resourceId={id} title="Customer files"/>
        </div>
      </div>

      {/* SMS conversation — includes messages linked via any of this customer's leads/estimates/jobs/invoices */}
      <div className="mt-6">
        <SmsThread scope="customer" id={id}/>
      </div>

      {/* Phase 2 — Email history for this customer */}
      <div className="mt-6">
        <EmailThread customerId={id} prefillTo={customer?.email || ""} title="Email history"/>
      </div>

      {/* Email threads — grouped conversation view (click opens full thread). */}
      <div className="mt-6">
        <CustomerEmailTab customerId={id}/>
      </div>

      {/* Phase 2 — Email compose dialog (opens from the "Email" button in the hero) */}
      <EmailComposeDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        customerId={id}
        prefillTo={customer?.email || ""}
      />

      {/* Reviews & Referrals (Iter43 Phase 1 — reviews only; referrals in Phase 3) */}
      <div className="mt-6">
        <CustomerReviewsSection customerId={id} />
      </div>

      {/* Properties (up to 20 per customer) — add, edit, delete + jump to
          Smart Estimator scoped to the chosen property. */}
      <div className="mt-6">
        <CustomerPropertiesSection customer={customer} />
      </div>

      {/* Iter57.2 — Saved measurements on this property (reuse across estimates). */}
      <div className="mt-6">
        <PropertyMeasurementsSection
          customer={customer}
          onOpenEstimate={(eid) => nav(`/app/estimates/${eid}`)}
        />
      </div>

      {/* Iter D.1 — Solar Property Intelligence. Only renders when a property
          exists for the customer. Reads /api/properties/{id}/solar-intel. */}
      {properties?.[0]?.id && (
        <div className="mt-6">
          <PropertySolarCard propertyId={properties[0].id} />
        </div>
      )}

      {/* Iter 58 — Property Service Revenue & Lifetime Value. Renders per
          property (each property has its own revenue/service history). Hidden
          for technician/sales roles at the component level; backend also
          enforces admin+ authorization. */}
      {properties?.map?.((p) => p?.id ? (
        <div key={`pv-${p.id}`} className="mt-6">
          <PropertyValueSummary propertyId={p.id} />
        </div>
      ) : null)}

      <NewInvoiceDialog
        open={invoiceDialogOpen}
        onClose={()=>setInvoiceDialogOpen(false)}
        customerId={customer.id}
        onCreated={(inv)=>{ setInvoiceDialogOpen(false); nav(`/app/invoices/${inv.id}`); }}
      />

      <Dialog open={editOpen} onOpenChange={(v)=>{ if (!v) setEditOpen(false); }}>
        <DialogContent className="sm:max-w-lg" data-testid="customer-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Edit customer</DialogTitle>
          </DialogHeader>
          {editForm && (
            <form onSubmit={saveEdit} className="space-y-3">
              <div>
                <Label>Name *</Label>
                <Input
                  required
                  value={editForm.name}
                  onChange={(e)=>setEditForm({...editForm, name: e.target.value})}
                  data-testid="customer-edit-name"
                  autoFocus
                />
              </div>
              <div>
                <Label>Company <span className="text-zinc-400 text-xs">(optional)</span></Label>
                <Input
                  value={editForm.company}
                  onChange={(e)=>setEditForm({...editForm, company: e.target.value})}
                  placeholder="Acme Roofing LLC"
                  data-testid="customer-edit-company"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e)=>setEditForm({...editForm, email: e.target.value})}
                    placeholder="customer@example.com"
                    data-testid="customer-edit-email"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={editForm.phone}
                    onChange={(e)=>setEditForm({...editForm, phone: e.target.value})}
                    placeholder="(555) 555-1234"
                    data-testid="customer-edit-phone"
                  />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={editForm.address}
                  onChange={(e)=>setEditForm({...editForm, address: e.target.value})}
                  placeholder="Street, City, State ZIP"
                  data-testid="customer-edit-address"
                />
                {editForm.address !== (customer.address || "") && (
                  <div className="text-[11px] text-amber-700 mt-1">
                    Address changes update the record but do not re-geocode Street View automatically.
                  </div>
                )}
              </div>
              <div>
                <Label>Lead source</Label>
                <Input
                  value={editForm.lead_source}
                  onChange={(e)=>setEditForm({...editForm, lead_source: e.target.value})}
                  placeholder="Referral, Google, Facebook, walk-in…"
                  data-testid="customer-edit-lead-source"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  rows={3}
                  value={editForm.notes}
                  onChange={(e)=>setEditForm({...editForm, notes: e.target.value})}
                  data-testid="customer-edit-notes"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={()=>setEditOpen(false)}
                  disabled={editSaving}
                  data-testid="customer-edit-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift"
                  disabled={editSaving}
                  data-testid="customer-edit-save"
                >
                  {editSaving ? "Saving…" : "Save changes"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  const cls = accent === "emerald" ? "text-emerald-700" : accent === "orange" ? "text-[#FF5A00]" : "text-zinc-900";
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4 card-crisp">
      <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">{label}</div>
      <div className={`font-heading text-2xl font-bold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
